import { useContext, useEffect, useState } from "react";
import { Grid, Divider, useTheme, Typography, Box } from "@mui/material";
import { WhiteBox } from "@foodpilot/foods";

import ScoreSection from "./components/ScoreSection";
import TrajectorySection from "./components/TrajectorySection";
import GoalSection from "./components/GoalSection";
import Legend from "./components/Legend";
import SectionDivider from "./components/SectionDivider";

import { useActionPlanData } from "./hooks/useActionPlanData";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { RootPropsContext } from "src/components/Puck/config/root";
import { isAppInPDFMode } from "src/utils/rendering";

export type ActionPlanProps = {
    actionPlanId?: string;
    scoreId?: number | null;
    variationId?: number | null;
    customGoalValue?: number;
    customGoalYear?: number;
    customGoalUnit?: string;
    showScoreSection?: boolean;
    showTrajectorySection?: boolean;
    showGoalSection?: boolean;
    showLegend?: boolean;
    actionPlanTitleVisible?: boolean;
};

const ActionPlan = (props: ActionPlanProps) => {
    const { scores = [] } = useFoodpilotContext();

    const [isInPuckEditor, setIsInPuckEditor] = useState(false);
    const theme = useTheme();
    const rootProps = useContext(RootPropsContext);
    const isPdfMode = rootProps?.pageType === "PDF" || isAppInPDFMode();

    useEffect(() => {
        const isPuckIframe = document.getElementById("preview-frame") !== null;
        setIsInPuckEditor(isPuckIframe);
    }, []);

    const {
        actionPlanId,
        scoreId,
        variationId,
        customGoalValue,
        customGoalYear,
        customGoalUnit,
        showScoreSection = true,
        showTrajectorySection = true,
        showGoalSection = true,
        showLegend = true,
        actionPlanTitleVisible,
    } = props;

    const planData = useActionPlanData({
        actionPlanId,
        scoreId,
        variationId,
        customGoalValue,
        customGoalYear,
        customGoalUnit,
    });

    const {
        isLoading,
        scoreTitle,
        diagnosticScore,
        diagnosticScoreChart,
        diagnosticMaxScore,
        diagnosticSSQCompletions,
        diagnosticCTAURL,
        projectedScore,
        projectedScoreChart,
        projectedMaxScore,
        projectedYearValue,
        actionCount,
        projectedCTAURL,
        goalValue,
        goalYear,
        goalUnit,
        unit,
        diagnosticToProjectedDifference,
        projectedToGoalDifference,
        diagnosticToGoalDifference,
        legendItems,
    } = planData;

    const visibleSections = [showScoreSection, showTrajectorySection, showGoalSection].filter(Boolean).length;
    const columnWidth = visibleSections > 1 ? 12 / visibleSections : 12;
    const sectionWidth = isPdfMode ? columnWidth : 12;
    const forceSideBySideDivider = isPdfMode && visibleSections > 1;
    const reserveDiagnosticSubtitleSpace = visibleSections > 1 && (showTrajectorySection || showGoalSection);
    const [isDiagonsticEnabled, isTrajectoryEnabled] = [
        showScoreSection && diagnosticScoreChart?.values.length > 0,
        showTrajectorySection && projectedScoreChart?.values.length > 0,
    ];

    const score = scores.find((score) => score.id === scoreId) || null;
    const variation = score?.variations.find((variation) => variation.id === variationId) || null;
    const displayedUnit = variation ? (variation.unit?.abbreviation ?? "") : unit;

    return (
        <Box sx={{ mb: theme.spacing(2) }}>
            {actionPlanTitleVisible && scoreTitle ?
                <Box sx={{ mb: theme.spacing(4) }}>
                    <Typography variant="h2">{scoreTitle}</Typography>
                </Box>
            :   <></>}
            <WhiteBox>
                <Grid container sx={{ position: "relative" }}>
                    {showScoreSection && (
                        <Grid item xs={sectionWidth} lg={columnWidth} sx={{ position: "relative" }}>
                            <ScoreSection
                                score={diagnosticScore}
                                scoreId={Number(scoreId)}
                                chartData={diagnosticScoreChart}
                                completionPercentage={diagnosticSSQCompletions}
                                reserveSubtitleSpace={reserveDiagnosticSubtitleSpace}
                                enabled={isDiagonsticEnabled}
                                unit={displayedUnit}
                                isLoading={!isInPuckEditor && isLoading}
                                ctaURL={diagnosticCTAURL}
                                maxScore={diagnosticMaxScore}
                            />
                            <SectionDivider
                                showDivider={visibleSections > 1}
                                showPercentage={isTrajectoryEnabled || (!showTrajectorySection && isDiagonsticEnabled)}
                                percentageDifference={
                                    showTrajectorySection ? diagnosticToProjectedDifference : diagnosticToGoalDifference
                                }
                                position="right"
                                forceSideBySide={forceSideBySideDivider}
                            />
                        </Grid>
                    )}

                    {showTrajectorySection && (
                        <Grid item xs={sectionWidth} lg={columnWidth} sx={{ position: "relative" }}>
                            <TrajectorySection
                                score={projectedScore ?? 0}
                                scoreId={Number(scoreId)}
                                chartData={projectedScoreChart}
                                year={projectedYearValue}
                                actionsTaken={actionCount}
                                enabled={isTrajectoryEnabled}
                                unit={displayedUnit}
                                ctaURL={projectedCTAURL}
                                isLoading={!isInPuckEditor && isLoading}
                                maxScore={projectedMaxScore}
                            />
                            <SectionDivider
                                showDivider={showGoalSection && visibleSections > 1}
                                showPercentage={isTrajectoryEnabled}
                                percentageDifference={projectedToGoalDifference}
                                position="right"
                                forceSideBySide={forceSideBySideDivider}
                            />
                        </Grid>
                    )}

                    {showGoalSection && (
                        <Grid item xs={sectionWidth} lg={columnWidth} sx={{ position: "relative" }}>
                            <GoalSection score={Number(goalValue.toFixed(2))} year={goalYear} unitLabel={goalUnit} />
                        </Grid>
                    )}
                </Grid>

                {showLegend && legendItems && legendItems.length > 0 && (
                    <>
                        <Divider />
                        <Legend items={legendItems} />
                    </>
                )}
            </WhiteBox>
        </Box>
    );
};

export default ActionPlan;
